import { cn } from '@/utils/cn'

interface RiskFactor {
  factor: string
  impact: number
  description: string
}

interface DefaultRiskCardProps {
  defaultProbability: number
  isHighRisk: boolean
  riskFactors: RiskFactor[]
  confidence: number
  horizonDays: number
  className?: string
}

export function DefaultRiskCard({
  defaultProbability,
  isHighRisk,
  riskFactors,
  confidence,
  horizonDays,
  className,
}: DefaultRiskCardProps) {
  const probabilityPercent = (defaultProbability * 100).toFixed(1)
  const confidencePercent = (confidence * 100).toFixed(0)

  return (
    <div
      className={cn(
        'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6',
        className
      )}
      aria-label="Default Risk Assessment"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Default Risk
        </h3>
        {isHighRisk && (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800"
            role="status"
            aria-label="High risk flag"
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            High Risk
          </span>
        )}
      </div>

      {/* Probability display */}
      <div className="mb-4">
        <div className="flex items-baseline gap-1">
          <span
            className={cn(
              'text-3xl font-bold',
              isHighRisk ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
            )}
          >
            {probabilityPercent}%
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            default probability
          </span>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Horizon: <span className="font-medium text-gray-700 dark:text-gray-300">{horizonDays} days</span>
        </p>
      </div>

      {/* Confidence meter */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-gray-600 dark:text-gray-400">Confidence</span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {confidencePercent}%
          </span>
        </div>
        <div
          className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2"
          role="progressbar"
          aria-valuenow={confidence * 100}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Confidence: ${confidencePercent}%`}
        >
          <div
            className="bg-blue-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${Math.min(confidence * 100, 100)}%` }}
          />
        </div>
      </div>

      {/* Risk factors */}
      {riskFactors.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Risk Factors
          </h4>
          <ul className="space-y-2" aria-label="Risk factors list">
            {riskFactors.map(({ factor, impact, description }) => (
              <li
                key={factor}
                className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-md"
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {factor}
                  </span>
                  <span
                    className={cn(
                      'text-xs font-medium px-1.5 py-0.5 rounded',
                      impact > 0.7
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        : impact > 0.4
                        ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                    )}
                  >
                    Impact: {(impact * 100).toFixed(0)}%
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {description}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
